import * as React from "react";
import { debounce } from "@cloudsuite/theme_library/scripts/helper/";
import { MatchHighlight } from "../../../ui/highlight";

function Suggestion({ suggestion, type, index, searchTerm, languageCode }) {
  const handleDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "send",
      eventCategory: "Search",
      eventAction: `Results: ${languageCode}`,
      eventLabel: `${encodeURIComponent(searchTerm).replaceAll('%20', '+').toLowerCase()}`,
    });
  };
  return (
    <a
      key={`${type}-${index}`}
      className="search-item__content-item"
      href={suggestion.url}
      onClick={handleDataLayer}
    >
      {type === "search" ? (
        <span className="icon fa fa-search me-2"></span>
      ) : null}
      {suggestion.picture ? (
        <img src={suggestion.picture} alt={suggestion.value} />
      ) : null}
      <MatchHighlight text={suggestion.value} tokens={searchTerm.split(/\s/)} />
      <span className="icon fa fa-angle-right ms-auto"></span>
    </a>
  );
}

function Suggestions({ results, searchTerm, suggestionTypes, languageCode }) {
  return suggestionTypes.map((type) => {
    const item = results[type];
    return item && item["data"] && item.data.length > 0 ? (
      <div className={`search-item ac_${type}`}>
        {type != "search" ? (
          <div className="search-item__title">
            {js_translations[`ac_${type}`] || "Title"}
          </div>
        ) : null}
        <div className="search-item__content">
          {type == "popular"
            ? [...item.data].map((_item, index) => (
                <Suggestion
                  key={index}
                  suggestion={_item}
                  type={type}
                  index={index}
                  searchTerm={searchTerm}
                  languageCode={languageCode}
                />
              ))
            : [...item.data]
                .slice(0, 3)
                .map((_item, index) => (
                  <Suggestion
                    key={index}
                    suggestion={_item}
                    type={type}
                    index={index}
                    searchTerm={searchTerm}
                    languageCode={languageCode}
                  />
                ))}
        </div>
      </div>
    ) : null;
  });
}

export function AutocompleteSearch({
  autocompleteUrl,
  formAction,
  minCharCount,
  placeholder,
  defaultValue,
  suggestionTypes,
  languageCode
}) {
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState({});
  const [searchTerm, setSearchTerm] = React.useState(defaultValue);
  const [showboxShadow, setShowBoxShadow] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const INPUT_REF = React.createRef(null);
  const SUGGESTION_TYPES = suggestionTypes || [
    "product",
    "search",
    "tree",
    "brand",
    "popular",
    "partnumber",
    "device",
    "part",
    "blog",
    "site_content",
  ];

  const debouncedHandleInputChange = React.useMemo(
    () => debounce(300, handleInputChange),
    []
  );

  if (!autocompleteUrl) {
    throw new Error(
      "autocompleteUrl must be defined as [data-autocomplete-url='']"
    );
  }

  if (!SUGGESTION_TYPES.length > 0) {
    throw new Error("There must be at least 1 suggestionType configured");
  }

  React.useEffect(() => {
    if (searchTerm) {
      getResults(searchTerm);
    }
  }, []);

  async function getResults(query) {
    setLoading(true);

    if (
      query &&
      query.trim() !== "" &&
      query.length >= Number(minCharCount || 3)
    ) {
      const url = `${autocompleteUrl}?search_term=${query}&c=${SUGGESTION_TYPES.join(
        "&c="
      )}`;
      const results = await fetch(url)
        .then((response) => response.json())
        .catch((response) => response);
      setLoading(false);
      setResults(results);
      return;
    }

    setLoading(false);
    setResults({});
  }

  function handleClearInput() {
    INPUT_REF.current.focus();
    INPUT_REF.current.value = "";
    setResults({});
    setSearchTerm("");
    setShowResults(false);
  }

  function handleInputChange(event) {
    setSearchTerm(event.target.value);
    getResults(event.target.value);
    if (event.target.value.trim() === "") {
      setShowResults(false);
    } else {
      setShowResults(true);
    }
  }

  function handleOnBlur(_) {
    setShowBoxShadow(false);
    setShowResults(false);
  }

  function handleOnFocus(_) {
    setShowBoxShadow(true);
    setShowResults(Object.keys(results).length > 0);
  }

  return (
    <form
      method="GET"
      action={formAction}
      className={`autocomplete-search ${showboxShadow ? "active" : ""}`}
      autoComplete="off"
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onSubmit={(e) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "send",
          eventCategory: "Search",
          eventAction: `Results: ${languageCode}`,
          eventLabel: `${encodeURIComponent(searchTerm).replaceAll('%20', '+').toLowerCase()}`,
        });
      }}
    >
      <div className="input-group icon-input-group flex">
        <span
          className={`icon fa fa-search ${searchTerm ? "has-search-term" : ""}`}
          aria-hidden="true"
        ></span>
        <input
          ref={INPUT_REF}
          type="search"
          name="search_term"
          placeholder={placeholder}
          defaultValue={defaultValue}
          className="form-control my-0"
          onChange={debouncedHandleInputChange}
        />
        <button
          className="btn btn-default hidden"
          type="submit"
          aria-label="Search"
          data-test="header-search-button"
        >
          <span className="icon fa fa-search"></span>
        </button>
        {searchTerm ? (
          <button onClick={handleClearInput} className="icon-btn" aria-label="Clear selection">
            <span className="icon gorb gorb-close-cross"></span>
          </button>
        ) : null}
      </div>
      <div
        data-js="autocomplete-search-results"
        data-cs-results-shown={showResults ? true : null}
        className={`autocomplete-search__results`}
      >
        {loading ? (
          <div className="loader-icon icon fa fa-spin fa-spinner"></div>
        ) : (
          <Suggestions
            suggestionTypes={SUGGESTION_TYPES}
            results={results}
            searchTerm={searchTerm}
            languageCode={languageCode}
          />
        )}
      </div>
    </form>
  );
}
